(() => {
  const config = window.NubiaTheme || {};
  const routes = config.routes || {};
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const bootstrapProducts = (window.__NUBIA_BOOTSTRAP__ && window.__NUBIA_BOOTSTRAP__.products) || [];
  const isEn = () => (document.body?.dataset.lang || document.documentElement.lang || "fr").startsWith("en");

  const ajaxUrl = (value, fallback) => {
    const url = String(value || fallback || "");
    return url.endsWith(".js") ? url : `${url}.js`;
  };

  let cartCurrency = config.currency || 'CAD';
  const money = (cents) => new Intl.NumberFormat(isEn() ? 'en-CA' : 'fr-CA', { style: 'currency', currency: cartCurrency, currencyDisplay: 'code' }).format(Number(cents || 0) / 100);

  // Bound both headers and JSON body. A timed-out write has an unknown outcome;
  // do not retry it automatically and risk adding an item twice.
  const requestCartJson = async (url, options = {}, timeoutMs = 15000) => {
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const error = new Error('Cart request timed out');
        error.name = 'TimeoutError';
        reject(error);
        controller.abort();
      }, timeoutMs);
    });
    try {
      return await Promise.race([timeout, (async () => {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const payload = await response.json();
        return { response, payload };
      })()]);
    } finally { clearTimeout(timer); }
  };
  const uncertainRequest = error => error instanceof TypeError || error instanceof SyntaxError || error.name === 'AbortError' || error.name === 'TimeoutError';

  const openPanel = (node) => {
    if (!node) return;
    node.classList.add("is-open");
    node.setAttribute("aria-hidden", "false");
  };

  const closePanel = (node) => {
    if (!node) return;
    node.classList.remove("is-open");
    node.setAttribute("aria-hidden", "true");
  };

  const esc = (value) => String(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");

  const renderCart = (cart) => {
    cartCurrency = cart.currency || config.currency || "CAD";
    $$("[data-cart-count]").forEach((node) => { node.textContent = String(cart.item_count || 0); });
    $$("[data-cart-total]").forEach((node) => { node.textContent = money(cart.total_price); });
    $$("[data-cart-checkout], [data-cart-update]").forEach((node) => { node.disabled = !cart.item_count; });
    $$("[data-cart-lines], [data-cart-page]").forEach((lines) => {
      const cartPage = lines.hasAttribute("data-cart-page");
      if (!cart.items?.length) {
        lines.innerHTML = `<p class="empty-state">${isEn() ? "Your cart is empty." : "Ton panier est vide."}</p>`;
        return;
      }

      lines.innerHTML = cart.items.map((item) => {
        const product = bootstrapProducts.find((entry) => entry.slug === item.handle);
        const productName = product?.name || item.product_title;
        const image = product?.tile || product?.image || item.image || "";
        return `<div class="cart-line" style="--accent:${product?.accent || "#c08552"};--soft:${product?.soft || "rgba(192, 133, 82, 0.18)"};">
        <img src="${esc(image)}" alt="${esc(productName)}" />
        <div><strong>${esc(productName)}</strong><small>${esc(item.variant_title || "")} / ${item.quantity} x ${money(item.final_price)}</small><label class="cart-line-quantity"><span>${isEn() ? "Quantity" : "Quantité"}</span><input type="number" min="${cartPage ? 0 : 1}" step="1" inputmode="numeric" value="${item.quantity}" ${cartPage ? 'name="updates[]"' : `data-cart-quantity="${esc(item.key)}" data-cart-quantity-original="${item.quantity}"`} aria-label="${esc((isEn() ? "Quantity for " : "Quantité pour ") + productName + (item.variant_title ? " — " + item.variant_title : ""))}"${pendingLineChanges.has(item.key) ? " disabled" : ""}></label></div>
        <button class="round-button" type="button" aria-label="${isEn() ? "Remove" : "Retirer"}" data-cart-remove="${esc(item.key)}"${pendingLineChanges.has(item.key) ? " disabled" : ""}>x</button>
      </div>`;
      }).join("");
    });
  };

  let cartReadVersion = 0;
  let cartMutation = Promise.resolve();
  const pendingForms = new WeakSet();
  const initialUnavailable = new WeakMap();
  const pendingRemovals = new WeakSet();
  const pendingLineChanges = new Map();
  const mutateCart = (action) => {
    const next = cartMutation.then(action, action);
    cartMutation = next.catch(() => {});
    return next;
  };

  const fetchCart = async () => {
    const version = ++cartReadVersion;
    const { response, payload: cart } = await requestCartJson(ajaxUrl(routes.cart, `${routes.root || "/"}cart`), { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Cart unavailable");
    if (version === cartReadVersion) renderCart(cart);
    return cart;
  };

  const setStatus = (form, message) => {
    let output = form ? $("[data-product-status]", form) : $("[data-cart-status]");
    if (!output) {
      output = document.createElement("p");
      output.className = form ? "product-form-status" : "cart-drawer-status";
      output.setAttribute(form ? "data-product-status" : "data-cart-status", "");
      (form || $("#cart-drawer") || document.body).append(output);
    }
    output.hidden = false;
    output.classList.remove("visually-hidden");
    output.setAttribute("role", "status");
    output.setAttribute("aria-live", "polite");
    output.textContent = message;
    if (!form) {
      const page = $("[data-cart-page]");
      if (page) {
        let pageStatus = $("[data-cart-page-status]");
        if (!pageStatus) {
          pageStatus = document.createElement("p");
          pageStatus.className = "cart-drawer-status";
          pageStatus.setAttribute("data-cart-page-status", "");
          pageStatus.setAttribute("role", "status");
          pageStatus.setAttribute("aria-live", "polite");
          page.after(pageStatus);
        }
        pageStatus.textContent = message;
      }
    }
  };

  const variantAvailable = (form) => {
    if (form.dataset.variantAvailable === "false") return false;
    const select = $("[data-variant-select]", form);
    const option = select?.selectedOptions?.[0];
    const selectedButton = $$(".variant-row [data-variant-id]", form).find((button) => button.dataset.variantId === select?.value);
    if (!select && !selectedButton) {
      if (!initialUnavailable.has(form)) initialUnavailable.set(form, !!$('button[type="submit"]', form)?.disabled);
      return !initialUnavailable.get(form);
    }
    return option?.dataset.available !== "false" && !option?.disabled && selectedButton?.dataset.variantAvailable !== "false";
  };

  const syncSubmit = (form) => {
    const available = variantAvailable(form);
    const disabled = pendingForms.has(form) || !available;
    const submit = $('button[type="submit"]', form);
    if (submit) submit.disabled = disabled;
    if (form.id === "NubiaProductForm") {
      $$("[data-submit-product]").forEach((button) => { button.disabled = disabled; });
      $$("[data-add-submit] span, [data-submit-product] span").forEach((label) => {
        const compact = !!label.closest("[data-submit-product]");
        label.dataset.fr = available ? (compact ? "Ajouter" : "Ajouter au panier") : "Sold out";
        label.dataset.en = available ? (compact ? "Add" : "Add to cart") : "Sold out";
        label.textContent = isEn() ? label.dataset.en : label.dataset.fr;
      });
    }
    form.setAttribute("aria-busy", pendingForms.has(form) ? "true" : "false");
  };

  const syncVariant = (form) => {
    if (!form) return;
    const select = $("[data-variant-select]", form);
    if (!select?.value) return;
    const buttons = $$(".variant-row [data-variant-id]", form);
    const selected = buttons.find((button) => button.dataset.variantId === select.value);
    buttons.forEach((button) => button.classList.toggle("active", button === selected));
    const price = Number(select.selectedOptions?.[0]?.dataset.price ?? selected?.dataset.variantPrice);
    if (Number.isFinite(price)) $$("[data-product-price], [data-mobile-dock-price]").forEach((node) => { node.textContent = money(price); });
    syncSubmit(form);
    const url = new URL(location.href);
    url.searchParams.set("variant", select.value);
    history.replaceState({}, "", url);
  };

  const lockCartLine = (key, disabled) => {
    $$("[data-cart-quantity]").filter((input) => input.dataset.cartQuantity === key).forEach((input) => { input.disabled = disabled; });
    $$("[data-cart-remove]").filter((button) => button.dataset.cartRemove === key).forEach((button) => { button.disabled = disabled; });
  };

  document.addEventListener("change", async (event) => {
    const select = event.target.closest("[data-variant-select]");
    if (select) syncVariant(select.closest("[data-product-form]"));
    const input = event.target.closest("[data-cart-quantity]");
    if (!input) return;
    const key = input.dataset.cartQuantity;
    if (pendingLineChanges.has(key)) return;
    const quantity = Number(input.value);
    const previous = Number(input.dataset.cartQuantityOriginal);
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      input.value = String(previous);
      setStatus(null, isEn() ? "Enter a whole quantity of at least 1. Use Remove to delete the item." : "Entre une quantité entière d'au moins 1. Utilise Retirer pour supprimer l'article.");
      return;
    }
    if (quantity === previous) return;
    pendingLineChanges.set(key, quantity);
    lockCartLine(key, true);
    setStatus(null, isEn() ? "Updating quantity..." : "Modification de la quantité...");
    try {
      await mutateCart(async () => {
        ++cartReadVersion;
        try {
          const { response, payload } = await requestCartJson(ajaxUrl(routes.cartChange, (routes.root || "/") + "cart/change"), { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: key, quantity }) });
          if (!response.ok) throw new Error(payload.description || payload.message || (isEn() ? "Unable to change this quantity." : "Impossible de modifier cette quantité."));
          ++cartReadVersion;
          pendingLineChanges.delete(key);
          renderCart(payload);
          setStatus(null, isEn() ? "Quantity updated." : "Quantité modifiée.");
        } catch (error) {
          // A lost response does not prove that Shopify rejected the change.
          input.value = String(previous);
          try { await fetchCart(); } catch {}
          throw error;
        }
      });
    } catch (error) {
      setStatus(null, uncertainRequest(error)
        ? (isEn() ? "The quantity change could not be confirmed. Check your cart before trying again." : "Le changement de quantité n'a pas pu être confirmé. Vérifie ton panier avant de réessayer.")
        : error.message);
    } finally {
      pendingLineChanges.delete(key);
      lockCartLine(key, false);
    }
  });

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest("[data-product-form]");
    if (!form || !window.fetch) return;
    event.preventDefault();
    if (pendingForms.has(form)) return;
    if (!variantAvailable(form)) {
      setStatus(form, isEn() ? "This size is currently sold out." : "Ce format est actuellement en rupture de stock.");
      syncSubmit(form);
      return;
    }
    const quantity = $('[name="quantity"]', form);
    if (quantity && (!Number.isSafeInteger(Number(quantity.value)) || Number(quantity.value) < 1)) {
      setStatus(form, isEn() ? "Enter a whole quantity of at least 1." : "Entre une quantité entière d'au moins 1.");
      quantity.focus();
      return;
    }
    const body = new FormData(form);
    pendingForms.add(form);
    syncSubmit(form);
    setStatus(form, isEn() ? "Adding..." : "Ajout...");
    setStatus(null, "");
    try {
      await mutateCart(async () => {
        ++cartReadVersion;
        const { response, payload } = await requestCartJson(ajaxUrl(routes.cartAdd, (routes.root || "/") + "cart/add"), { method: "POST", headers: { Accept: "application/json" }, body });
        if (!response.ok) {
          let message = payload.description || payload.message || (isEn() ? "Unable to add this item." : "Impossible d'ajouter cet article.");
          // Shopify can add the available stock while returning 422 for the excess.
          try { await fetchCart(); }
          catch { message += isEn() ? " Check your cart before trying again." : " Vérifie ton panier avant de réessayer."; }
          throw new Error(message);
        }
        let message = isEn() ? "Added to cart." : "Ajouté au panier.";
        try { await fetchCart(); }
        catch {
          message = isEn() ? "Item added. The preview could not refresh. Open View cart to check it before adding again." : "Article ajouté. L'aperçu n'a pas pu s'actualiser. Ouvre Voir le panier pour vérifier avant de réessayer.";
        }
        setStatus(form, message);
        setStatus(null, message);
        openPanel($("#cart-drawer"));
      });
    } catch (error) {
      const message = uncertainRequest(error)
        ? (isEn() ? "The connection was interrupted. Check your cart before trying again." : "La connexion a été interrompue. Vérifie ton panier avant de réessayer.")
        : error.message;
      setStatus(form, message);
    } finally {
      pendingForms.delete(form);
      syncSubmit(form);
    }
  });

  document.addEventListener("click", async (event) => {
    const cartToggle = event.target.closest("[data-cart-toggle]");
    const cartClose = event.target.closest("[data-cart-close]");
    const remove = event.target.closest("[data-cart-remove]");
    const variantButton = event.target.closest("[data-variant-id]");
    const minus = event.target.closest("[data-qty-minus]");
    const plus = event.target.closest("[data-qty-plus]");
    const mobileSubmit = event.target.closest("[data-submit-product]");

    if (cartToggle) {
      event.preventDefault();
      openPanel($("#cart-drawer"));
      fetchCart().catch(() => { setStatus(null, isEn() ? "Unable to refresh the cart. Open View cart or try again." : "Impossible d'actualiser le panier. Ouvre Voir le panier ou réessaie."); });
    }
    if (cartClose) closePanel($("#cart-drawer"));

    if (remove) {
      event.preventDefault();
      if (pendingLineChanges.has(remove.dataset.cartRemove)) return;
      if (pendingRemovals.has(remove)) return;
      pendingRemovals.add(remove);
      remove.disabled = true;
      setStatus(null, isEn() ? "Removing..." : "Suppression...");
      try {
        await mutateCart(async () => {
          ++cartReadVersion;
          const { response, payload } = await requestCartJson(ajaxUrl(routes.cartChange, (routes.root || "/") + "cart/change"), { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: remove.dataset.cartRemove, quantity: 0 }) });
          if (!response.ok) throw new Error(payload.description || payload.message || (isEn() ? "Unable to remove this item. Try again." : "Impossible de retirer cet article. Réessaie."));
          ++cartReadVersion;
          renderCart(payload);
          setStatus(null, isEn() ? "Item removed." : "Article retiré.");
        });
      } catch (error) {
        const message = uncertainRequest(error)
          ? (isEn() ? "The connection was interrupted. Refresh your cart to check whether the item was removed." : "La connexion a été interrompue. Actualise ton panier pour vérifier si l'article a été retiré.")
          : error.message;
        setStatus(null, message);
      } finally {
        pendingRemovals.delete(remove);
        remove.disabled = false;
      }
    }

    if (variantButton && variantButton.closest(".variant-row")) {
      const form = variantButton.closest("[data-product-form]");
      const select = form ? $("[data-variant-select]", form) : null;
      if (!select) return;
      select.value = variantButton.dataset.variantId;
      syncVariant(form);
    }

    if (minus || plus) {
      const row = event.target.closest(".quantity-row");
      const input = row ? $("[data-qty-input]", row) : null;
      if (input) {
        const current = Number(input.value);
        input.value = String(Math.max(1, (Number.isFinite(current) ? Math.floor(current) : 1) + (plus ? 1 : -1)));
      }
    }
    if (mobileSubmit && !mobileSubmit.disabled) $("#NubiaProductForm")?.requestSubmit();
  });

  const initProductGallery = (gallery) => {
    const slides = $$("[data-gallery-slide]", gallery);
    const thumbs = $$("[data-gallery-thumb]", gallery);
    const status = $("[data-gallery-status]", gallery);
    const message = $("[data-gallery-message]", gallery);
    if (!slides.length) return;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
    let requested = index;
    let revision = 0;
    const pendingImages = new WeakMap();

    const setMessage = (fr, en) => {
      if (!message) return;
      message.dataset.fr = fr;
      message.dataset.en = en;
      message.textContent = isEn() ? en : fr;
    };
    const imageReady = (image) => {
      if (pendingImages.has(image)) return pendingImages.get(image);
      const ready = new Promise((resolve, reject) => {
        let settled = false;
        let timer;
        const finish = (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          image.removeEventListener("load", loaded);
          image.removeEventListener("error", failed);
          if (error) reject(error); else resolve();
        };
        const failed = () => finish(new Error("Gallery image unavailable"));
        const loaded = async () => {
          if (!image.naturalWidth) return failed();
          try {
            if (image.decode) await image.decode();
            finish();
          } catch { failed(); }
        };
        image.addEventListener("load", loaded);
        image.addEventListener("error", failed);
        timer = setTimeout(failed, 15000);
        // Hidden lazy images must be requested before changing the visible slide.
        image.loading = "eager";
        if (image.complete) {
          if (image.naturalWidth) loaded(); else failed();
        }
      });
      pendingImages.set(image, ready);
      ready.then(() => pendingImages.delete(image), () => pendingImages.delete(image));
      return ready;
    };
    const commit = (next) => {
      index = next;
      slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
        slide.setAttribute("aria-hidden", String(i !== index));
      });
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle("is-active", i === index);
        thumb.setAttribute("aria-current", String(i === index));
      });
      if (status) status.textContent = `${index + 1} / ${slides.length}`;
    };
    const show = async (next) => {
      if (!Number.isInteger(next)) return;
      const target = (next % slides.length + slides.length) % slides.length;
      const currentRevision = ++revision;
      requested = target;
      if (target === index) {
        gallery.setAttribute("aria-busy", "false");
        setMessage("", "");
        return;
      }
      gallery.setAttribute("aria-busy", "true");
      slides[target].fetchPriority = "high";
      try {
        await imageReady(slides[target]);
        // A slow previous click must never replace the latest requested photo.
        if (currentRevision !== revision) return;
        commit(target);
        setMessage("", "");
      } catch {
        if (currentRevision !== revision) return;
        requested = index;
        setMessage("Impossible de charger cette photo. La photo précédente reste affichée.", "This photo could not load. The previous photo remains visible.");
      } finally {
        if (currentRevision === revision) gallery.setAttribute("aria-busy", "false");
      }
    };
    $("[data-gallery-prev]", gallery)?.addEventListener("click", () => show(requested - 1));
    $("[data-gallery-next]", gallery)?.addEventListener("click", () => show(requested + 1));
    thumbs.forEach((thumb) => thumb.addEventListener("click", () => show(Number(thumb.dataset.galleryThumb))));
    gallery.addEventListener("keydown", (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!event.target.closest("[data-gallery-thumb], [data-gallery-prev], [data-gallery-next]")) return;
      const target = event.key === "ArrowLeft" ? requested - 1 : event.key === "ArrowRight" ? requested + 1 : event.key === "Home" ? 0 : event.key === "End" ? slides.length - 1 : null;
      if (target === null) return;
      event.preventDefault();
      show(target);
      if (event.target.matches("[data-gallery-thumb]")) thumbs[requested]?.focus();
    });
    commit(index);
    gallery.setAttribute("aria-busy", "false");
    if (slides.length > 1) {
      $("[data-gallery-controls], .gallery-controls", gallery)?.removeAttribute("hidden");
      $("[data-gallery-thumbs], .gallery-thumbs", gallery)?.removeAttribute("hidden");
    }
    // The parser requests all five small responsive images immediately: the
    // first at high priority, the rest at low priority. Decode them before use.
    // There is no serial request chain and no dependence on a first click.
    thumbs.forEach((thumb, i) => {
      const prepare = () => { slides[i].fetchPriority = "high"; imageReady(slides[i]).catch(() => {}); };
      thumb.addEventListener("pointerenter", prepare);
      thumb.addEventListener("focus", prepare);
    });
    imageReady(slides[index]).then(() => {
      slides.forEach((slide, i) => { if (i !== index) imageReady(slide).catch(() => {}); });
    }).catch(() => {});
    imageReady(slides[index]).catch(() => {
      if (revision === 0) setMessage("Impossible de charger la photo. Choisis une autre photo.", "The photo could not load. Choose another photo.");
    });
  };
  $$("[data-product-gallery]").forEach(initProductGallery);

  const filters = $("[data-catalogue-filters]");
  if (filters) filters.addEventListener("click", (event) => {
    const button = event.target.closest("[data-filter]");
    if (!button) return;
    $$("[data-filter]", filters).forEach((node) => node.classList.toggle("active", node === button));
    const filter = button.dataset.filter;
    $$("[data-product-card]").forEach((card) => {
      const methods = card.dataset.methods || "";
      card.hidden = !(filter === "all" || (filter === "milk" && /latte|cappuccino|flat white|milk/.test(methods)) || (filter === "filter" && /filter|filtre|pour-over/.test(methods)) || (filter === "espresso" && /espresso/.test(methods)));
    });
  });

  $$("[data-product-form]").forEach((form) => {
    syncSubmit(form);
    form.classList.add("nubia-product-enhanced");
  });
  // The cart page is already rendered by Shopify. Preserve native form edits
  // instead of replacing them with a late initial Ajax response.
  if (!$("[data-cart-form]")) fetchCart().catch(() => {});
})();
